import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import { ServicingProvider } from './context/ServicingContext';

// Import API client to initialize interceptors
import './utils/apiClient';

// Import constants
import { ROUTES, USER_ROLES } from './utils/constants';

// Common components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import ProtectedRoute from './components/common/ProtectedRoute';

// Core pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';

// Rental Pages
// Servicing Pages
import ServicingHome from './pages/servicing/ServicingHome';
import HomePage from './pages/servicing/HomePage';
import Services from './pages/servicing/Services';
// ✅ RENAMED: Add "Servicing" prefix to avoid conflict
// import RefundPolicyServicing from './pages/servicing/RefundPolicy';
import TermsAndConditionsServicing from './pages/servicing/TermsAndConditions';
import Cart from './pages/servicing/Cart';
import ServicePackagesPage from './pages/servicing/ServicePackagesPage';
import CheckoutPage from './pages/servicing/CheckoutPage';
import ServiceHistory from './pages/servicing/ServiceHistory';
import ViewInvoice from "./pages/servicing/ViewInvoice"; 
// ✅ Quicklinks components (these will be the main ones used in routes)
import AboutUs from './components/common/quicklinks/AboutUs';
import ContactUs from './components/common/quicklinks/ContactUs';
import Faq from './components/common/quicklinks/Faq';
import RefundPolicy from './components/common/quicklinks/RefundPolicy';
import ShippingPolicy from './components/common/quicklinks/ShippingPolicy';
import ReturnPolicy from './components/common/quicklinks/ReturnPolicy';
import TermsAndConditions from './components/common/quicklinks/TermsAndConditions';


import InvestorPage from './components/common/Investors';

// Environment variables
const isDevelopment = import.meta.env.MODE === 'development';
const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_BASE_URL || 'http://localhost:8081';
const appVersion = import.meta.env.VITE_APP_VERSION || '2.0.0';

console.log('🔧 APP - Environment:', {
  mode: import.meta.env.MODE,
  isDev: isDevelopment,
  apiUrl: apiUrl,
  version: appVersion,
});

// ✅ ScrollToTop Component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Navbar component to dynamically switch based on route
function NavbarComponent() {
  const location = useLocation();
  const currentPath = location.pathname;

  const isBuySellRoute =
    currentPath.startsWith('/buysell') ||
    currentPath === '/sell' ||
    currentPath.startsWith('/vehicle-details') ||
    currentPath.startsWith('/callseller') ||
    currentPath.startsWith('/textseller') ||
    currentPath === '/enquiries' ||
    currentPath.startsWith('/chat') ||
    currentPath.startsWith('/owner-details');

  return isBuySellRoute ? <BuySellNavbar /> : <Navbar />;
}

// Enhanced 404 Not Found Component
const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
    <div className="text-center max-w-lg mx-auto p-8 bg-white rounded-2xl shadow-lg">
      <div className="mb-8">
        <h1 className="text-5xl font-bold text-gray-900 mb-2">404</h1>
        <h2 className="text-xl font-semibold text-gray-700 mb-4">Page Not Found</h2>
        <p className="text-gray-600 mb-6">
          The page you're looking for took a detour and got lost in the bike lanes!
        </p>
      </div>

      {isDevelopment && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6 text-sm">
          <p className="text-yellow-800 mb-2 font-medium">🔧 Debug Info:</p>
          <p className="text-yellow-700 text-left">
            Current URL: <code className="bg-yellow-100 px-1 rounded">{window.location.pathname}</code>
          </p>
          <p className="text-yellow-700 text-left">
            Search Params: <code className="bg-yellow-100 px-1 rounded">{window.location.search || 'None'}</code>
          </p>
          <p className="text-yellow-700 text-left">
            Environment: <code className="bg-yellow-100 px-1 rounded">{import.meta.env.MODE}</code>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a
          href="/"
          className="flex items-center justify-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          🏠 Go Home
        </a>
        <a
          href="/servicing"
          className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          🔧 Servicing
        </a>
       
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          If you think this is a mistake, please contact our support team.
        </p>
      </div>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
            <ServicingProvider>

                  <ScrollToTop />
                  <div className="min-h-screen bg-gray-50 flex flex-col">
                    <NavbarComponent />
                    <main className="flex-1">
                      <Routes>
                        {/* PUBLIC ROUTES */}
                        <Route path="/" element={<ServicingHome />} />
                        <Route
                          path="/login"
                          element={
                            <ProtectedRoute requireAuth={false} redirectIfAuthenticated={true}>
                              <Login />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/register"
                          element={
                            <ProtectedRoute requireAuth={false} redirectIfAuthenticated={true}>
                              <Register />
                            </ProtectedRoute>
                          }
                        />

                        {/* USER PROFILE */}
                        

                        <Route
                          path="/service-history"
                          element={
                            <ProtectedRoute requireAuth={true}>
                              <ServiceHistory />
                            </ProtectedRoute>
                          }
                        />

                        {/* ✅ QUICKLINKS ROUTES - Using components from quicklinks folder */}
                        <Route path="/about-us" element={<AboutUs />} />
                        <Route path="/contact-us" element={<ContactUs />} />
                        <Route path="/faq" element={<Faq />} />
                        <Route path="/refund-policy" element={<RefundPolicy />} />
                        <Route path="/shipping-policy" element={<ShippingPolicy />} />
                        <Route path="/return-policy" element={<ReturnPolicy />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

                        <Route path="/investor" element={<InvestorPage/>}/>

                        {/* RENTAL ROUTES */}
                        
                      
                       

                        {/* SERVICING ROUTES */}
                        <Route path="/servicing" element={<ServicingHome />} />
                        <Route path="/servicing/home" element={<HomePage />} />
                        <Route path="/services/sdsdsd" element={<Services />} />
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute requireAuth={true}>
                              <Profile />
                            </ProtectedRoute>
                          }
                        />
                          <Route
                    path="/invoice/:bookingId"
                    element={
                      <ProtectedRoute requireAuth={true}>
                        <ViewInvoice />
                      </ProtectedRoute>
                    }
                  />
                        {/* ✅ FIXED: Use the renamed servicing components */}
                        {/* <Route path="/servicing/refund" element={<RefundPolicyServicing />} /> */}
                        <Route path="/servicing/terms" element={<TermsAndConditionsServicing />} />
                        <Route
                          path="/servicing/cart"
                          element={
                            <ProtectedRoute requireAuth={true}>
                              <Cart />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/service-packages" element={<ServicePackagesPage />} />
                        <Route
                          path="/checkout"
                          element={
                            <ProtectedRoute requireAuth={true}>
                              <CheckoutPage />
                            </ProtectedRoute>
                          }
                        />

                        {/* DOCUMENT VERIFICATION ROUTE */}
                      

                        {/* SPAREPARTS ROUTES */}
                       


                        {/* STATIC PAGES - Use quicklinks components */}
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/refund" element={<RefundPolicy />} />
                        <Route path="/privacy" element={<RefundPolicy />} />
                        <Route path="/about" element={<AboutUs />} />
                        <Route path="/contact" element={<ContactUs />} />



                        {/* ADMIN ROUTES */}
                        <Route
                          path="/admin/*"
                          element={
                            <ProtectedRoute requireAuth={true} requiredRole={USER_ROLES.ADMIN} showRoleError={true}>
                              <div className="min-h-screen bg-gray-50 py-8">
                                <div className="max-w-4xl mx-auto px-4">
                                  <div className="bg-white rounded-lg shadow p-8 text-center">
                                    <div className="text-6xl mb-4">👨‍💼</div>
                                    <h1 className="text-3xl font-bold text-gray-900 mb-4">Admin Dashboard</h1>
                                    <p className="text-gray-600 mb-6">Welcome to the Ve go Admin Panel</p>
                                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                                      <p className="text-blue-800">Admin functionality is under development.</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </ProtectedRoute>
                          }
                        />

                        {/* DEVELOPMENT ROUTES */}
                        {isDevelopment && (
                          <Route
                            path="/debug"
                            element={
                              <div className="min-h-screen bg-gray-50 py-8">
                                <div className="max-w-4xl mx-auto px-4">
                                  <div className="bg-white rounded-lg shadow-lg p-8">
                                    <h1 className="text-3xl font-bold mb-6">🔧 Debug Panel</h1>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      <div className="bg-green-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-semibold mb-3 text-green-800">Rental Routes</h3>
                                        <div className="space-y-2">
                                          <a href="/rental" className="block text-green-600 hover:underline">
                                            Rental Home
                                          </a>
                                          <a href="/bikes" className="block text-green-600 hover:underline">
                                            Bike List
                                          </a>
                                          <a href="/profile" className="block text-green-600 hover:underline">
                                            User Profile
                                          </a>
                                        </div>
                                      </div>
                                      <div className="bg-blue-50 p-4 rounded-lg">
                                        <h3 className="text-lg font-semibold mb-3 text-blue-800">🔐 Auth Routes</h3>
                                        <div className="space-y-2">
                                          <a href="/login" className="block text-blue-600 hover:underline">
                                            Login
                                          </a>
                                          <a href="/register" className="block text-blue-600 hover:underline">
                                            Register
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            }
                          />
                        )}

                        {/* 404 CATCH ALL */}
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </main>
                    <Footer />
                  </div>
            </ServicingProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
